import { FC, ReactNode, Ref, useEffect, useRef } from 'react';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { ESHOP_TEST_ID } from '@telialt/core-eshop/src/test/test-ids';
import { Price } from '@telialt/models/src/EshopModel';
import { DDComponent, setupTeliaLtFrontEnd } from '@telialt/utils/src/datadog/setup';

import { LineData, Prices } from './model';
import { OfferLine } from './OfferLine';
import { OfferPriceAmounts } from './OfferPriceAmounts';
import { OfferPriceContainer, OfferPriceLabel, OldPriceTextAndLabel } from './ProductOfferPrices.styles';
import { formatPrice, resolvePriceDisplayData } from './utils';

type Props = {
    showMonthlyPrices: boolean;
    prices: Prices;
    ref: Ref<HTMLElement> | null;
};

export const ProductOfferPricesView: FC<Props> = ({ showMonthlyPrices, prices, ref }) => {
    const { t } = useTranslations();
    const displayData = resolvePriceDisplayData(prices, showMonthlyPrices, t);
    const isErrorLoggedRef = useRef(false);

    useEffect(() => {
        if (!displayData && !isErrorLoggedRef.current) {
            const datadogLogs = setupTeliaLtFrontEnd();
            datadogLogs.logger.warn('Product has incorrect prices', {
                prices,
                component: DDComponent.EshopProductCore,
            });
            isErrorLoggedRef.current = true;
        }
    }, [displayData, prices]);

    if (!displayData) {
        return null;
    }

    const { oldPrice, primaryLine, secondaryLine } = displayData;

    const testId = primaryLine.monthlyPrice
        ? ESHOP_TEST_ID.PRICE_WITH_MONTHLY_PAYMENT
        : ESHOP_TEST_ID.PRICE_WITHOUT_MONTHLY_PAYMENT;

    const getMonthlyA11yLabel = (price: Price) =>
        `${formatPrice(price)} ${t['eshop.product_page.monthly_price_unit_accessible']}`;
    const getTotalA11yLabel = (price: Price) => `${formatPrice(price)} ${price.currency}`;

    const getLineAriaLabel = (line: LineData, includeOldPrice = false) => {
        return [
            line.monthlyPrice && getMonthlyA11yLabel(line.monthlyPrice),
            line.monthlyPrice && t['eshop.product_page.product_offer_prices.price_separator'],
            getTotalA11yLabel(line.totalPrice),
            line.label,
            includeOldPrice &&
                oldPrice &&
                `${t['eshop.product_page.old_price_accessible']} ${getTotalA11yLabel(oldPrice)}`,
        ]
            .filter(Boolean)
            .join(' ');
    };

    const wrap = (children: ReactNode) => (
        <OfferPriceContainer data-test={testId} ref={ref as Ref<HTMLDivElement>}>
            {children}
        </OfferPriceContainer>
    );

    // US case 1/2 (leasing): bundle price (monthly + total).
    if (secondaryLine && primaryLine.monthlyPrice && secondaryLine.monthlyPrice) {
        return wrap(
            <>
                <OfferLine
                    line={primaryLine}
                    accessible={getLineAriaLabel(primaryLine, true)}
                    dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
                >
                    <OfferPriceAmounts
                        line={primaryLine}
                        color="accent"
                        currencyBold={false}
                        size="md"
                        trailing={<OfferPriceLabel>{primaryLine.label}</OfferPriceLabel>}
                    />
                </OfferLine>

                <OfferLine
                    line={secondaryLine}
                    accessible={getLineAriaLabel(secondaryLine)}
                    dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_SECONDARY_LINE}
                >
                    <OfferPriceAmounts
                        line={secondaryLine}
                        color="black"
                        currencyBold={false}
                        trailing={<OfferPriceLabel>{secondaryLine.label}</OfferPriceLabel>}
                    />
                </OfferLine>
            </>,
        );
    }

    // US case 1/2 (full price): bundle price (total only).
    if (secondaryLine) {
        return wrap(
            <>
                <OfferLine
                    line={primaryLine}
                    accessible={getLineAriaLabel(primaryLine)}
                    dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
                >
                    <OfferPriceAmounts
                        line={primaryLine}
                        color="accent"
                        size="lg"
                        trailing={<OfferPriceLabel>{primaryLine.label}</OfferPriceLabel>}
                    />
                </OfferLine>
                <OfferLine
                    line={secondaryLine}
                    accessible={getLineAriaLabel(secondaryLine)}
                    dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_SECONDARY_LINE}
                >
                    <OfferPriceAmounts
                        line={secondaryLine}
                        color="black"
                        trailing={<OfferPriceLabel>{secondaryLine.label}</OfferPriceLabel>}
                    />
                </OfferLine>
            </>,
        );
    }

    // US case 3 (leasing): standalone discount with old price (monthly + total + old total).
    if (oldPrice && primaryLine.monthlyPrice) {
        return wrap(
            <OfferLine
                line={primaryLine}
                accessible={getLineAriaLabel(primaryLine, true)}
                dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
            >
                <OfferPriceAmounts
                    line={primaryLine}
                    color="accent"
                    currencyBold={false}
                    size="md"
                    trailing={
                        <OldPriceTextAndLabel>{`${formatPrice(oldPrice)} ${oldPrice.currency}`}</OldPriceTextAndLabel>
                    }
                />
            </OfferLine>,
        );
    }

    // US case 3 (full price): standalone discount with old price (total + old total).
    if (oldPrice) {
        return wrap(
            <OfferLine
                line={primaryLine}
                accessible={getLineAriaLabel(primaryLine, true)}
                dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
            >
                <OfferPriceAmounts
                    line={primaryLine}
                    color="accent"
                    size="lg"
                    trailing={
                        <OldPriceTextAndLabel>{`${formatPrice(oldPrice)} ${oldPrice.currency}`}</OldPriceTextAndLabel>
                    }
                />
            </OfferLine>,
        );
    }

    // US case 4 (leasing): regular monthly + total price.
    if (primaryLine.monthlyPrice) {
        return wrap(
            <OfferLine
                line={primaryLine}
                accessible={getLineAriaLabel(primaryLine)}
                dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
            >
                <OfferPriceAmounts line={primaryLine} size="md" />
            </OfferLine>,
        );
    }

    // US case 4 (full price): regular total-only price.
    return wrap(
        <OfferLine
            line={primaryLine}
            accessible={getLineAriaLabel(primaryLine)}
            dataTest={ESHOP_TEST_ID.PRODUCT_OFFER_PRICE_PRIMARY_LINE}
        >
            <OfferPriceAmounts line={primaryLine} color="black" size="lg" />
        </OfferLine>,
    );
};
